package er.prateek.crossly.ui.screens.crossword

import er.prateek.crossly.data.model.Clue
import er.prateek.crossly.data.model.ClueDirection
import er.prateek.crossly.game.crossword.CellPos
import er.prateek.crossly.game.crossword.CrosswordEngine

/**
 * Clue-level navigation built on top of [CrosswordEngine]'s cell-level API.
 *
 * The engine deliberately exposes only `selectCell` (which toggles direction on
 * a repeat tap) — these helpers turn that into the moves the UI needs: jump to a
 * named clue, step to the next/previous one, and know which cells belong to the
 * word currently under the cursor. Keeping this in the UI layer leaves the game
 * state machine and its tests untouched.
 */

fun CrosswordEngine.cluesFor(direction: ClueDirection): List<Clue> =
    if (direction == ClueDirection.ACROSS) puzzle.cluesAcross else puzzle.cluesDown

/**
 * Moves the cursor to [clue] and guarantees the given [direction].
 *
 * A second `selectCell` on the same position is the engine's direction toggle,
 * so one conditional repeat lands on the requested orientation from any
 * starting state.
 */
fun CrosswordEngine.selectClue(clue: Clue, direction: ClueDirection) {
    val pos = CellPos(clue.row, clue.col)
    selectCell(pos)
    if (this.direction != direction) selectCell(pos)
}

/**
 * Steps [delta] clues through the current direction's list, wrapping into the
 * other direction at either end so repeated "next" walks the whole puzzle.
 */
fun CrosswordEngine.stepClue(delta: Int) {
    val current = currentClue() ?: return
    val clues = cluesFor(direction)
    val index = clues.indexOfFirst { it.number == current.number && it.row == current.row && it.col == current.col }
    if (index < 0) return

    val target = index + delta
    if (target in clues.indices) {
        selectClue(clues[target], direction)
        return
    }

    val otherDirection = if (direction == ClueDirection.ACROSS) ClueDirection.DOWN else ClueDirection.ACROSS
    val otherClues = cluesFor(otherDirection)
    if (otherClues.isEmpty()) {
        // Single-direction puzzle: wrap within this list rather than stalling.
        selectClue(clues[if (target < 0) clues.lastIndex else 0], direction)
        return
    }
    selectClue(
        if (target < 0) otherClues.last() else otherClues.first(),
        otherDirection,
    )
}

/** Every cell of a word, in reading order. */
fun wordCells(clue: Clue, direction: ClueDirection): List<CellPos> {
    val dr = if (direction == ClueDirection.DOWN) 1 else 0
    val dc = if (direction == ClueDirection.ACROSS) 1 else 0
    return clue.answer.indices.map { i -> CellPos(clue.row + dr * i, clue.col + dc * i) }
}

/** Every cell of the word under the cursor — drives the grid's word highlight. */
fun CrosswordEngine.currentWordCells(): Set<CellPos> {
    val clue = currentClue() ?: return emptySet()
    return wordCells(clue, direction).toSet()
}

/** True once every square of a word holds the right letter. */
fun CrosswordEngine.isClueSolved(clue: Clue, direction: ClueDirection): Boolean =
    wordCells(clue, direction).all { cellAt(it)?.isResolved == true }

/**
 * Jumps to the next word that still needs letters, wrapping past the end.
 *
 * Used when the rack is scoped to one word: finishing that word would otherwise
 * leave the player looking at an empty rack with no obvious next move. Does
 * nothing when every word is done — the solved state takes over from there.
 */
fun CrosswordEngine.selectNextUnsolvedClue() {
    val all = puzzle.cluesAcross.map { it to ClueDirection.ACROSS } +
        puzzle.cluesDown.map { it to ClueDirection.DOWN }
    if (all.isEmpty()) return

    val current = currentClue()
    val startIndex = all.indexOfFirst { (clue, dir) ->
        dir == direction && current != null && clue.row == current.row && clue.col == current.col
    }
    for (offset in 1..all.size) {
        val (clue, dir) = all[(startIndex.coerceAtLeast(0) + offset) % all.size]
        if (!isClueSolved(clue, dir)) {
            selectClue(clue, dir)
            return
        }
    }
}

/** Flips between Across and Down without moving the cursor. */
fun CrosswordEngine.toggleDirection() {
    selected?.let { selectCell(it) }
}

/**
 * Arrow-key movement: steps to the next playable cell in a direction, skipping
 * over black squares rather than stopping at them.
 */
fun CrosswordEngine.moveSelection(rowStep: Int, colStep: Int) {
    val from = selected ?: return
    var row = from.row + rowStep
    var col = from.col + colStep
    while (row in 0 until height && col in 0 until width) {
        val cell = cellAt(CellPos(row, col))
        if (cell != null && !cell.isBlack) {
            selectCell(CellPos(row, col))
            return
        }
        row += rowStep
        col += colStep
    }
}

/**
 * The squares the player has to fill, and how many they have. Starter letters are left out of
 * both: they arrive pre-filled, so counting them made an untouched puzzle read "44% complete".
 */
fun CrosswordEngine.fillProgress(): Pair<Int, Int> {
    val open = cells.filter { !it.isBlack && !it.isGiven }
    return open.count { it.userChar != null } to open.size
}

/** Anything worth saving: a letter typed or revealed, or a hint spent. Time alone isn't progress. */
fun CrosswordEngine.hasPlayerInput(): Boolean =
    fillProgress().first > 0 || hintsUsed > 0 || lettersRevealed > 0
